// Lógica pura de la ficha de Mercado Libre en Atlas: tipos de lo que devuelve el backend y
// cómo contar el resultado de un cambio. Sin red ni DOM (se prueba con vitest).
// Ver ADR-2026-09-28-mercadolibre-precio-y-descripcion-con-reglas (atlas-backend).

export interface PublicacionDelProducto {
  item_id: string;
  titulo: string | null;
  modelo: string;
  estado: string | null;
  /** Viene como texto (numeric de Postgres). */
  precio: string | null;
  moneda: string | null;
  precio_automatico: boolean;
  es_catalogo: boolean;
  permalink: string | null;
  miniatura: string | null;
  vendidos: number | null;
  visitas_30d: number | null;
}

export interface CalidadDeMl {
  puntaje: number | null;
  nivel: string | null;
  faltan_fotos: boolean;
  recomendaciones: Array<{ titulo: string; modo: "oportunidad" | "advertencia"; link: string | null }>;
  sin_calcular: boolean;
}

export type ResultadoCampo = { ok: true } | { ok: false; codigo: string; motivo: string };

/** Los 409 que se resuelven confirmando (no son errores: son un "¿seguro?"). */
export const PIDEN_CONFIRMAR = new Set(["cambio_grande", "saca_promocion"]);

/** Lee un precio escrito a mano: "$ 25.000", "25000", "25.000,50". null si no es un número. */
export function leerPrecioEscrito(texto: string): number | null {
  const limpio = texto.replace(/[$\s]/g, "");
  if (!limpio) return null;
  // Formato argentino: punto de miles y coma decimal.
  const normal = /,\d{1,2}$/.test(limpio) ? limpio.replace(/\./g, "").replace(",", ".") : limpio.replace(/[.,](?=\d{3}(\D|$))/g, "");
  const n = Number(normal);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
}

export function formatoPrecio(precio: number | string | null, moneda: string | null = "ARS"): string {
  const n = typeof precio === "string" ? Number(precio) : precio;
  if (n === null || !Number.isFinite(n)) return "—";
  return `${moneda === "USD" ? "US$" : "$"} ${n.toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;
}

/** "Precio actualizado." / el motivo de Mercado Libre, por campo. */
export function resumenDeCambio(r: { precio?: ResultadoCampo; descripcion?: ResultadoCampo } | null): { texto: string; ok: boolean } {
  if (!r) return { texto: "El cambio se está aplicando. En unos minutos lo vas a ver en Mercado Libre.", ok: true };
  const partes: string[] = [];
  let ok = true;
  if (r.precio) {
    if (r.precio.ok) partes.push("Precio actualizado.");
    else {
      ok = false;
      partes.push(`El precio no se cambió: ${r.precio.motivo}`);
    }
  }
  if (r.descripcion) {
    if (r.descripcion.ok) partes.push("Descripción actualizada.");
    else {
      ok = false;
      partes.push(`La descripción no se cambió: ${r.descripcion.motivo}`);
    }
  }
  return { texto: partes.join(" ") || "Listo.", ok };
}

const ESTADOS: Record<string, string> = {
  active: "Activa",
  paused: "Pausada",
  closed: "Finalizada",
  under_review: "En revisión",
  inactive: "Inactiva",
};

export function estadoLegible(estado: string | null): string {
  return (estado && ESTADOS[estado]) || "—";
}
